/**
 * FAQ Section
 * Fetches FAQs from CMS API
 */
import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const FAQ_CATEGORY_ID = 40;
const FAQ_SECTION_UID = 'FAQ-SECTION';

// Strip HTML from description for answer text
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch FAQ section header
        const sectionResponse = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: FAQ_SECTION_UID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (sectionResponse.data?.code === 1 && Array.isArray(sectionResponse.data?.data) && sectionResponse.data.data.length > 0) {
          const data = sectionResponse.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setSectionData({
              Title: data.Title,
              SubTitle: data.SubTitle,
              Description: data.Description,
            });
          }
        }

        // Fetch FAQ items
        const response = await axios.post(
          `${CMS_API_URL}info/by-category`,
          { CategoryId: FAQ_CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          const validFaqs = response.data.data
            .filter(item => item.IsDeleted === false && item.IsPublished === true)
            .map(item => ({
              question: item.Title,
              answer: stripHtml(item.Description),
            }))
            .filter(faq => faq.question && faq.answer);
          setFaqs(validFaqs);
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && faqs.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div>
            <div className="text-center mb-10">
              <div className="h-4 w-16 bg-gray-200 rounded mx-auto mb-2 animate-pulse" />
              <div className="h-8 w-80 bg-gray-200 rounded mx-auto mb-4 animate-pulse" />
              <div className="w-24 h-1 bg-gray-200 mx-auto mb-4 animate-pulse" />
              <div className="h-4 w-96 bg-gray-200 rounded mx-auto animate-pulse" />
            </div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      {/* FAQ Section */}
      <div className="text-center mb-6">
              <span className="text-yellow-600 font-semibold text-sm uppercase tracking-wide">
                {sectionData.Title || ''}
              </span>
            <h2 className="text-3xl font-bold text-[#2f3951] mt-2">
              {sectionData?.SubTitle || ''}
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
            {sectionData?.Description && (
              <p className="text-gray-600 mt-4">
                {stripHtml(sectionData.Description)}
              </p>
            )}
          </div>

          {/* FAQ List */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <HelpCircle className="w-5 h-5 text-[#2f3951] mr-3 flex-shrink-0" />
                    <span className="font-semibold text-[#2f3951]">{faq.question}</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="p-4 bg-white">
                    <p className="text-gray-600 pl-8">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
    </div>
  );
};

export default FAQSection;
